import { createReducer, on } from '@ngrx/store';
import { PersonDetailsForm } from '@qivos/qivoscloud-ion-components/lib/models/person-details.model';
import { Person } from '@qivos/qivoscloud-ng-api';
import {
  InitMobileNumber,
  InitQCCode,
  Token,
  UpdatePersonData,
  UpdatePersonDataFailed,
  UpdatePersonDataSuccess,
  searchPersonByTelephoneSuccess,
} from '../actions/ms-registration-form.actions';

export interface MsRegistrationFormState extends PersonDetailsForm {
  person: Person;
  token: string;
  qcCode: string;
  message: string;
  showMessage: boolean;
  fName: string;
  lName: string;
  cNumber: string;
}

function calculateDates() {
  const today = new Date();

  const minValidDate = new Date();
  minValidDate.setFullYear(today.getFullYear() - 18);
  const dateOfBirthMaxValue = minValidDate.toISOString();

  const maxValidDate = new Date();
  maxValidDate.setFullYear(today.getFullYear() - 95);
  const dateOfBirthMinValue = maxValidDate.toISOString();
  return { dateOfBirthMaxValue, dateOfBirthMinValue };
}

const initialState: MsRegistrationFormState = {
  person: new Person(),
  token: '',
  qcCode: '',
  mobileNumber: {
    rendered: true,
    required: false,
    label: 'Κινητό/Mobile',
    placeholder: 'Please add your mobile phone number.',
    value: '',
    validation: /^69[0-9]*\d*$/,
    message:
      'The phone number doesn’t exist. Please write a correct phone number.',
    disabled: true,
  },
  gender: {
    rendered: true,
    required: false,
    label: 'Τίτλος/Title',
    maleGenderLabel: 'Κύριος/Mr',
    femaleGenderLabel: 'Κυρία/Ms',
    otherGenderLabel: 'Άλλο/Other',
    value: { Name: '', Value: '' },
  },
  firstName: {
    rendered: true,
    required: true,
    disabled: false,
    label: 'Όνομα/Name',
    placeholder: '',
    value: '',
    validation: /^[α-ωίϊΐόάέύϋΰήώΑ-ΩΆΈΊΌΎΏΉa-zA-Zs.-]+$/,
    message:
      'Τα στοιχεία που καταχωρήσατε δεν είναι σωστά/Please write a correct first name',
  },
  lastName: {
    rendered: true,
    required: true,
    disabled: false,
    label: 'Επίθετο/Surname',
    placeholder: '',
    value: '',
    validation: /^[α-ωίϊΐόάέύϋΰήώΑ-ΩΆΈΊΌΎΏΉa-zA-Zs.-]+$/,
    message:
      'Τα στοιχεία που καταχωρήσατε δεν είναι σωστά/Please write a correct last name',
  },
  email: {
    rendered: true,
    required: true,
    label: 'Email',
    placeholder: 'Το email σας/Your email',
    value: '',
    validation:
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+(@[A-Za-z0-9.-]{2,})+\.[A-Za-z]{2,}/,
    message:
      'Το email που καταχωρήσατε δεν είναι σωστό/Please write a correct email.',
  },
  region: {
    rendered: true,
    required: false,
    label: 'Νομός/Region',
    placeholder: 'Επιλέξτε νομό/Select region.',
    value: '',
    message: 'Please write a correct region.',
  },
  town: {
    rendered: true,
    required: false,
    label: 'Περιοχή/Town ',
    placeholder: 'Πληκτρολογήστε περιοχή/Type your town',
    value: '',
  },
  zipCode: {
    rendered: true,
    required: false,
    label: 'Τ.Κ./ ZIP',
    placeholder: '',
    value: '',
    validation: /^[0-9]{5}$/,
    message: 'Τα στοιχεία που καταχωρήσατε δεν είναι σωστά',
  },
  dateOfBirth: {
    rendered: true,
    required: false,
    label: 'Ημερ. γέννησης/ Date of Birth',
    placeholder: 'HH/MM/YYYY',
    datePickerDoneButtonLabel: 'Ok',
    datePickerCancelButtonLabel: 'Cancel',
    value: '',
    minValue: calculateDates().dateOfBirthMinValue,
    maxValue: calculateDates().dateOfBirthMaxValue,
    message:
      'Το μέλος πρέπει να είναι άνω των 18 ετών/Member must be over 18 years old',
  },
  nameDate: {
    rendered: false,
    required: false,
    nameDateDayPlaceholder: 'Date',
    nameDateMonthPlaceholder: 'Month',
    nameDateLabel: 'Date of name date',
    nameDateDayValue: '',
    nameDateMonthValue: '',
    errorMessage: 'Please select a valid name day. First select the month.',
  },
  singleConsent: {
    rendered: true,
    required: false,
    label: 'Τρόποι επικοινωνίας/ Communication',
  },
  smsConsent: {
    rendered: true,
    label: 'SMS',
    value: true,
  },
  viberConsent: {
    rendered: true,
    label: 'instant messages (Viber κλπ)',
    value: true,
  },
  emailConsent: {
    rendered: true,
    label: 'Email',
    value: true,
  },
  multiConsent: {
    rendered: false,
    required: false,
    label: '',
  },
  allConsent: {
    rendered: false,
    label: 'Με όλα τα παραπάνω',
    value: false,
  },
  message: '',
  showMessage: false,
  fName: '',
  lName: '',
  cNumber: '',
};

export const MsRegistrationFormReducer = createReducer(
  initialState,
  on(Token, (state, { token }) => ({
    ...state,
    token,
  })),
  on(searchPersonByTelephoneSuccess, (state, { persons }) => ({
    ...state,
    person: persons !== undefined ? persons[0] : { ...state.person },
    firstName: {
      ...state.firstName,
      value: persons && persons[0].firstName ? persons[0].firstName : '',
    },
    lastName: {
      ...state.lastName,
      value: persons && persons[0].lastName ? persons[0].lastName : '',
    },
    email: {
      ...state.email,
      value:
        persons && persons[0].emailList && persons[0].emailList[0]
          ? (persons[0].emailList.find((elist) => elist.isPrimary === true)
              ?.emailAddress ?? '')
          : '',
    },
    smsConsent: {
      ...state.smsConsent,
      value:
        persons && persons[0] && persons[0].consentList
          ? (persons[0].consentList.find(
              (consent) => consent.name === 'MS NEWS_AND_GENERAL_OFFERS SMS ',
            )?.flag ?? state.smsConsent.value)
          : state.smsConsent.value,
    },
    viberConsent: {
      ...state.viberConsent,
      value:
        persons && persons[0] && persons[0].consentList
          ? (persons[0].consentList.find(
              (consent) => consent.name === 'MS NEWS_AND_GENERAL_OFFERS VIBER ',
            )?.flag ?? state.viberConsent.value)
          : state.viberConsent.value,
    },
    emailConsent: {
      ...state.emailConsent,
      value:
        persons && persons[0] && persons[0].consentList
          ? (persons[0].consentList.find(
              (consent) => consent.name === 'MS NEWS_AND_GENERAL_OFFERS EMAIL ',
            )?.flag ?? state.emailConsent.value)
          : state.emailConsent.value,
    },
    multiConsent: {
      ...state.multiConsent,
      value:
        persons && persons[0] && persons[0].consentList
          ? ((persons[0].consentList.find(
              (consent) => consent.name === 'MS NEWS_AND_GENERAL_OFFERS SMS ',
            )?.flag &&
              persons[0].consentList.find(
                (consent) =>
                  consent.name === 'MS NEWS_AND_GENERAL_OFFERS VIBER ',
              )?.flag &&
              persons[0].consentList.find(
                (consent) =>
                  consent.name === 'MS NEWS_AND_GENERAL_OFFERS EMAIL ',
              )?.flag) ??
            false)
          : false,
    },
  })),
  on(InitMobileNumber, (state, { mobileNumber }) => ({
    ...state,
    mobileNumber: { ...state.mobileNumber, value: mobileNumber },
  })),
  on(InitQCCode, (state, { qcCode }) => ({
    ...state,
    qcCode,
  })),
  on(UpdatePersonData, (state) => ({
    ...state,
    message: '',
    showMessage: false,
  })),
  on(UpdatePersonDataSuccess, (state, { response }) => ({
    ...state,
    message: 'Συγχαρητήρια, μόλις ολοκληρώσατε επιτυχώς την εγγραφή σας',
    showMessage: true,
    fName: response.payload.data.firstName,
    lName: response.payload.data.lastName,
    cNumber: response.payload.data.loyaltyMembershipData[0].cardNumber,
  })),
  on(UpdatePersonDataFailed, (state) => ({
    ...state,
    message: 'Υπήρξε ένα πρόβλημα κατά την εγγραφή σας',
    showMessage: true,
  })),
);
